/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { api } from '@/lib/api'
import type { CurrencyOption } from '@/lib/format-quota'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { SettingsSection } from '../components/settings-section'

interface ValidationError {
  message: string
}

function validateRows(rows: CurrencyOption[]): ValidationError | null {
  if (rows.length === 0) {
    return { message: 'At least one currency is required' }
  }

  const defaults = rows.filter((r) => r.is_default)
  if (defaults.length !== 1) {
    return { message: 'Exactly one currency must be set as default' }
  }

  const codes = rows.map((r) => r.code.toUpperCase()).filter(Boolean)
  const uniqueCodes = new Set(codes)
  if (uniqueCodes.size !== codes.length) {
    return { message: 'Duplicate currency codes are not allowed' }
  }

  for (const r of rows) {
    if (!r.code.trim()) {
      return { message: 'Currency code must not be empty' }
    }
    if (!r.symbol.trim()) {
      return { message: 'Currency symbol must not be empty' }
    }
    if (!Number.isFinite(r.rate) || r.rate <= 0) {
      return { message: 'Rate must be a positive number' }
    }
  }

  const allLocales: string[] = []
  for (const r of rows) {
    for (const loc of r.locales) {
      allLocales.push(loc)
    }
  }
  const uniqueLocales = new Set(allLocales)
  if (uniqueLocales.size !== allLocales.length) {
    return { message: 'Duplicate locales are not allowed across currencies' }
  }

  return null
}

export function CurrencyDisplaySection() {
  const { t } = useTranslation()
  const [rows, setRows] = useState<CurrencyOption[]>([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ currencies: CurrencyOption[] }>('/api/option/display_currencies')
      .then((res) => {
        setRows(res.data.currencies ?? [])
      })
      .catch(() => {
        // Silently ignore load errors; rows stay empty
      })
  }, [])

  const addRow = () => {
    setRows([
      ...rows,
      {
        code: '',
        symbol: '',
        rate: 1,
        locales: [],
        enabled: true,
        is_default: rows.length === 0,
      },
    ])
    setDirty(true)
    setValidationError(null)
  }

  const updateRow = (i: number, patch: Partial<CurrencyOption>) => {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
    setDirty(true)
    setValidationError(null)
  }

  const removeRow = (i: number) => {
    const next = rows.filter((_, idx) => idx !== i)
    // If we removed the default, make the first row default
    const hasDefault = next.some((r) => r.is_default)
    if (!hasDefault && next.length > 0) {
      next[0] = { ...next[0], is_default: true }
    }
    setRows(next)
    setDirty(true)
    setValidationError(null)
  }

  const setDefault = (i: number) => {
    setRows(rows.map((r, idx) => ({ ...r, is_default: idx === i })))
    setDirty(true)
    setValidationError(null)
  }

  const save = async () => {
    const err = validateRows(rows)
    if (err) {
      setValidationError(err.message)
      return
    }
    setSaving(true)
    try {
      await api.post('/api/option/display_currencies', { currencies: rows })
      toast.success(t('Saved'))
      setDirty(false)
      setValidationError(null)
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : t('Save failed')
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsSection
      title={t('Currency Display')}
      description={t(
        'Configure display currencies for user balance. Each currency can map to one or more browser locales.'
      )}
    >
      <div className='space-y-4'>
        <div className='overflow-x-auto rounded-md border'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b border-border bg-accent/40'>
                <th className='px-3 py-2 text-left font-medium'>
                  {t('Code')}
                </th>
                <th className='px-3 py-2 text-left font-medium'>
                  {t('Symbol')}
                </th>
                <th className='px-3 py-2 text-left font-medium'>
                  {t('Rate (vs CNY)')}
                </th>
                <th className='px-3 py-2 text-left font-medium'>
                  {t('Locales (comma-separated)')}
                </th>
                <th className='px-3 py-2 text-center font-medium'>
                  {t('Default')}
                </th>
                <th className='px-3 py-2 text-center font-medium'>
                  {t('Enabled')}
                </th>
                <th className='px-3 py-2 text-center font-medium'>
                  {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className='text-muted-foreground px-3 py-6 text-center'
                  >
                    {t('No currencies configured. Click "+ Add currency" to begin.')}
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} className='border-b last:border-0'>
                  <td className='px-3 py-2'>
                    <Input
                      value={r.code}
                      onChange={(e) =>
                        updateRow(i, {
                          code: e.target.value.toUpperCase(),
                        })
                      }
                      maxLength={3}
                      className='h-8 w-16 font-mono uppercase'
                      placeholder='CNY'
                    />
                  </td>
                  <td className='px-3 py-2'>
                    <Input
                      value={r.symbol}
                      onChange={(e) =>
                        updateRow(i, { symbol: e.target.value })
                      }
                      maxLength={4}
                      className='h-8 w-14'
                      placeholder='¥'
                    />
                  </td>
                  <td className='px-3 py-2'>
                    <Input
                      type='number'
                      step='0.01'
                      min='0.0001'
                      value={r.rate}
                      onChange={(e) =>
                        updateRow(i, { rate: Number(e.target.value) })
                      }
                      className='h-8 w-24'
                    />
                  </td>
                  <td className='px-3 py-2'>
                    <Input
                      value={r.locales.join(',')}
                      onChange={(e) =>
                        updateRow(i, {
                          locales: e.target.value
                            .split(',')
                            .map((s) => s.trim().toLowerCase())
                            .filter(Boolean),
                        })
                      }
                      placeholder='zh,zh-CN'
                      className='h-8 w-36'
                    />
                  </td>
                  <td className='px-3 py-2 text-center'>
                    <input
                      type='radio'
                      name='currency-default'
                      checked={r.is_default ?? false}
                      onChange={() => setDefault(i)}
                      className='cursor-pointer'
                      aria-label={t('Set as default')}
                    />
                  </td>
                  <td className='px-3 py-2 text-center'>
                    <Checkbox
                      checked={r.enabled}
                      onCheckedChange={(checked) =>
                        updateRow(i, { enabled: checked === true })
                      }
                      aria-label={t('Enabled')}
                    />
                  </td>
                  <td className='px-3 py-2 text-center'>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => removeRow(i)}
                    >
                      {t('Remove')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {validationError && (
          <p className='text-destructive text-sm'>{t(validationError)}</p>
        )}

        <div className='flex items-center gap-3'>
          <Button variant='outline' size='sm' onClick={addRow}>
            {t('+ Add currency')}
          </Button>
          {dirty && (
            <Badge variant='secondary'>{t('Unsaved changes')}</Badge>
          )}
          <Button
            onClick={save}
            disabled={!dirty || saving}
            size='sm'
          >
            {saving ? t('Saving…') : t('Save')}
          </Button>
        </div>
      </div>
    </SettingsSection>
  )
}
